import {LatLngExpression} from 'leaflet';
import {FC, useMemo} from 'react';
import {MapContainer, Marker, Popup, TileLayer} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import {styled} from 'styled-components';

import {TrackingEvent} from '@mavismart_frontend/stores/tracking_store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GeoEvent {
  email: string;
  action: string;
  ts: number;
  city?: string;
  latitude: number;
  longitude: number;
}

interface GeoMapProps {
  events: TrackingEvent[] | undefined;
  locale?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FRANCE_CENTER: LatLngExpression = [46.6, 2.3];
const DEFAULT_ZOOM = 5;
const MAX_MARKERS = 200;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPopupDate(ts: number, locale: string | undefined): string {
  return new Date(ts).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GeoMap: FC<GeoMapProps> = ({events, locale}) => {
  const geoEvents = useMemo<GeoEvent[]>(() => {
    if (!events) {
      return [];
    }
    return events
      .filter(
        (ev): ev is TrackingEvent & {geo: {latitude: number; longitude: number}} =>
          ev.geo?.latitude !== undefined && ev.geo?.longitude !== undefined
      )
      .slice(0, MAX_MARKERS)
      .map(ev => ({
        email: ev.email,
        action: ev.action,
        ts: ev.ts,
        city: ev.geo.city,
        latitude: ev.geo.latitude,
        longitude: ev.geo.longitude,
      }));
  }, [events]);

  return (
    <MapWrapper>
      <MapTitle>🗺️ Localisation des connexions</MapTitle>
      <StyledMapContainer center={FRANCE_CENTER} zoom={DEFAULT_ZOOM} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerClusterGroup>
          {geoEvents.map((ev, i) => (
            <Marker key={`${ev.email}-${ev.ts}-${i}`} position={[ev.latitude, ev.longitude]}>
              <Popup>
                <PopupContent>
                  <PopupRow>
                    <strong>Email :</strong> {ev.email}
                  </PopupRow>
                  <PopupRow>
                    <strong>Action :</strong> {ev.action}
                  </PopupRow>
                  <PopupRow>
                    <strong>Date :</strong> {formatPopupDate(ev.ts, locale)}
                  </PopupRow>
                  {ev.city ? (
                    <PopupRow>
                      <strong>Ville :</strong> {ev.city}
                    </PopupRow>
                  ) : null}
                </PopupContent>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </StyledMapContainer>
    </MapWrapper>
  );
};

GeoMap.displayName = 'GeoMap';

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const MapWrapper = styled.div`
  background: #f8f9fb;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MapTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #2f3a4f;
`;

const StyledMapContainer = styled(MapContainer)`
  height: 350px;
  width: 100%;
  border-radius: 8px;
  z-index: 0;
`;

const PopupContent = styled.div`
  font-size: 13px;
  line-height: 1.5;
`;

const PopupRow = styled.div`
  white-space: nowrap;
`;
